import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

/**
 * Cinematic intro loader that plays once per visit.
 *  - Dark curtain drop with gold “drop” seal line
 *  - Logo pulsing in from center
 *  - Tagline typewriter-style reveal
 *  - Exits with scale-up fade, revealing the main 3D scene
 */
export function IntroLoader({ onDone }) {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh");
  const [phase, setPhase] = useState(0); // 0 curtain, 1 reveal, 2 exit

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 700);
    const t2 = setTimeout(() => setPhase(2), 2600);
    const t3 = setTimeout(() => onDone?.(), 3400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onDone]);

  return (
    <AnimatePresence>
      {phase < 2 && (
        <motion.div
          key="intro"
          data-testid="intro-loader"
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{
            background:
              "radial-gradient(ellipse at center, #0A1628 0%, #05070B 70%)",
          }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } }}
        >
          {/* Top and bottom “curtain” bars that slide open */}
          <motion.div
            aria-hidden="true"
            className="absolute left-0 right-0 top-0 bg-ink-950"
            initial={{ height: "50%" }}
            animate={{ height: phase === 0 ? "50%" : "0%" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          />
          <motion.div
            aria-hidden="true"
            className="absolute left-0 right-0 bottom-0 bg-ink-950"
            initial={{ height: "50%" }}
            animate={{ height: phase === 0 ? "50%" : "0%" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          />

          {/* Gold center seal line */}
          <motion.div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2"
            style={{
              translateX: "-50%",
              translateY: "-50%",
              height: 1,
              background:
                "linear-gradient(90deg, rgba(240,196,90,0) 0%, rgba(240,196,90,0.9) 50%, rgba(240,196,90,0) 100%)",
              boxShadow: "0 0 24px rgba(240,196,90,0.55)",
            }}
            initial={{ width: "0%", opacity: 0 }}
            animate={{
              width: phase === 0 ? "70%" : "0%",
              opacity: phase === 0 ? 1 : 0,
            }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Logo + Wordmark */}
          <motion.div
            className="relative flex flex-col items-center gap-6"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{
              opacity: phase === 0 ? 0 : 1,
              scale: phase === 0 ? 0.92 : 1,
            }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          >
            <div
              className="relative"
              style={{
                width: 140,
                height: 140,
                animation: "logoPulse 2.6s ease-in-out infinite",
              }}
            >
              <img
                src="/assets/poseidon-logo-v3.png"
                alt="Poseidon Music"
                draggable="false"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>

            <div className="flex flex-col items-center gap-2">
              <motion.div
                initial={{ opacity: 0, y: 8, letterSpacing: "0.5em" }}
                animate={{
                  opacity: phase === 0 ? 0 : 1,
                  y: phase === 0 ? 8 : 0,
                  letterSpacing: phase === 0 ? "0.5em" : "0.04em",
                }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
                className="font-display-en text-4xl sm:text-5xl text-[var(--paper-50)] text-shadow-depth"
                style={{ fontWeight: 700 }}
              >
                Poseidon
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: phase === 0 ? 0 : 1 }}
                transition={{ duration: 0.9, delay: 1.0 }}
                className="text-[11px] tracking-[0.5em] uppercase font-mono-brand text-[var(--gold-400)]"
              >
                {isZh ? "海 神 · M U S I C" : "海 神  ·  M U S I C"}
              </motion.div>
            </div>

            {/* Progress tick */}
            <motion.div
              className="mt-8 h-[1px] overflow-hidden"
              style={{
                width: 160,
                background: "rgba(255,255,255,0.08)",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: phase === 0 ? 0 : 1 }}
              transition={{ duration: 0.6, delay: 1.1 }}
            >
              <motion.div
                className="h-full"
                initial={{ width: "0%" }}
                animate={{ width: phase >= 1 ? "100%" : "0%" }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 1.1 }}
                style={{
                  background:
                    "linear-gradient(90deg, rgba(240,196,90,0) 0%, rgba(240,196,90,1) 100%)",
                }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
